/**
 * `@ixigo/fetch` extended with one extra per-call option,
 * `includeSearchParams`: page-URL params (`window.location.search`) to
 * forward onto the request as query params.
 *
 * ```ts
 * import fetch from '@/lib/fetch';
 *
 * fetch.post('cabs/api/v1/fareDetails', {
 *   body,
 *   includeSearchParams: ['source'],
 * });
 * ```
 *
 * Client-side only (SSR no-op); explicitly set params always win; params
 * absent from the page URL are not sent.
 */
import fetch, {
	type BeforeRequestHook,
	type FetchInstance,
	type Input,
	type Options,
} from '@ixigo/fetch';

/** Allowlist: array of keys, or a map of key → enabled. */
export type IncludedSearchParams =
	| readonly string[]
	| Readonly<Record<string, boolean>>;

export interface ExtendedOptions extends Options {
	/** Page-URL params to forward onto this request. */
	includeSearchParams?: IncludedSearchParams;
}

const toKeys = (config: IncludedSearchParams): readonly string[] =>
	Array.isArray(config)
		? (config as readonly string[])
		: Object.keys(config).filter(
				(key) => (config as Record<string, boolean>)[key],
			);

/** Resolves `includeSearchParams` (ky passes custom options to hooks). */
export const includeSearchParamsHook: BeforeRequestHook = (
	request,
	options,
) => {
	const config = (options as ExtendedOptions).includeSearchParams;
	if (!config || typeof window === 'undefined') return;
	const pageParams = new URLSearchParams(window.location.search);
	const url = new URL(request.url);
	let changed = false;
	for (const key of toKeys(config)) {
		const value = pageParams.get(key);
		if (value && !url.searchParams.has(key)) {
			url.searchParams.set(key, value);
			changed = true;
		}
	}
	if (!changed) return;
	// Only the URL changes (a request's URL is read-only): rebuild with the
	// same options ky built `request` from — full fidelity without enumerating
	// fields (Request ignores unknown option keys).
	return new Request(url, {
		...(options as RequestInit),
		// `options.headers` is only what the call site passed — take the
		// request's headers so ones added by earlier hooks aren't lost.
		headers: request.headers,
	});
};

/**
 * `FetchInstance` whose request methods (members callable as
 * `(input, options)`) also accept `ExtendedOptions`.
 */
export type ExtendedFetchInstance = FetchInstance & {
	[K in keyof FetchInstance as FetchInstance[K] extends (
		input: Input,
		options?: Options,
	) => unknown
		? K
		: never]: <T>(
		input: Input,
		options?: ExtendedOptions,
	) => ReturnType<typeof fetch.post<T>>;
};

// NOTE: keep this hook last — ky stops the beforeRequest chain once a hook
// returns a Request, so hooks after this one would be skipped when it fires.
const extendedFetch: ExtendedFetchInstance = fetch.extend({
	hooks: { beforeRequest: [includeSearchParamsHook] },
});

export default extendedFetch;
